import { useMemo, useState } from 'react'
import {
  addMonths,
  buildMonth,
  isoLocal,
  monthLabel,
  weightOf,
  type DayCell,
} from '@core/calendar'
import type { LetterTask } from '@core/letters'
import type { Appointment } from '@core/appointments'
import { Button } from '@design/components'
import { formatCents } from '@core/money'
import type { Payable } from '@core/payments'
import { DaySheet } from './DaySheet'

const WEEKDAYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo']

/**
 * The month, as daily totals.
 *
 * spec.md §18: totals, not stacked items — a month of thirty supplier names is
 * unreadable on a phone, and the question a calendar answers better than a
 * list is "which days are heavy". Weight is relative to the busiest day in
 * view, because an absolute scale would paint a quiet month white and a
 * restaurant's month black.
 *
 * Payments and deadlines are told apart by **shape and label**, never colour
 * alone: a filled bar for money, a hollow ring for a deadline. It has to
 * survive colour-blindness and a greyscale screenshot.
 */
export function CalendarView({
  payables,
  tasks,
  appointments,
  userId,
  onChanged,
  onNewAppointment,
  onOpenAppointment,
}: {
  payables: Payable[]
  tasks: LetterTask[]
  appointments: Appointment[]
  userId: string
  onChanged: () => void
  onNewAppointment: () => void
  onOpenAppointment: (a: Appointment) => void
}) {
  const [anchor, setAnchor] = useState(() => isoLocal(new Date()).slice(0, 7) + '-01')
  const [openDay, setOpenDay] = useState<string | null>(null)

  const cells = useMemo(
    () => buildMonth(anchor, payables, tasks, appointments),
    [anchor, payables, tasks, appointments],
  )
  const max = useMemo(
    () => Math.max(0, ...cells.filter((c) => c.inMonth).map((c) => c.dueCents + c.estimatedCents)),
    [cells],
  )
  const monthTotal = cells
    .filter((c) => c.inMonth)
    .reduce((sum, c) => sum + c.dueCents, 0)

  return (
    <div className="px-4 pb-4">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setAnchor((a) => addMonths(a, -1))}
          className="w-11 h-11 -ml-2 grid place-items-center text-muted"
          aria-label="Vorige maand"
        >
          ‹
        </button>
        <div className="text-center">
          <p className="font-medium capitalize">{monthLabel(anchor)}</p>
          {monthTotal > 0 && (
            <p className="text-xs text-muted tabular-nums">{formatCents(monthTotal)}</p>
          )}
        </div>
        <button
          onClick={() => setAnchor((a) => addMonths(a, 1))}
          className="w-11 h-11 -mr-2 grid place-items-center text-muted"
          aria-label="Volgende maand"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((day) => (
          <span key={day} className="text-center text-[0.6875rem] text-muted">
            {day}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell) => (
          <Day key={cell.date} cell={cell} max={max} onOpen={() => setOpenDay(cell.date)} />
        ))}
      </div>

      <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-1 rounded-full bg-ink" aria-hidden /> betaling
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full border-[1.5px] border-ink" aria-hidden /> deadline
        </span>
        <span className="flex items-center gap-1.5">
          {/* A third shape, not a third colour. Bar, ring, square — each still
              tells itself apart in greyscale and to a colour-blind eye. */}
          <span className="w-2 h-2 bg-ink" aria-hidden /> afspraak
        </span>
      </div>

      <div className="mt-4">
        <Button tone="secondary" onClick={onNewAppointment}>
          Afspraak inplannen
        </Button>
      </div>

      {openDay && (
        <DaySheet
          date={openDay}
          payables={payables}
          tasks={tasks}
          appointments={appointments}
          userId={userId}
          onChanged={onChanged}
          onOpenAppointment={onOpenAppointment}
          onClose={() => setOpenDay(null)}
        />
      )}
    </div>
  )
}

function Day({ cell, max, onOpen }: { cell: DayCell; max: number; onOpen: () => void }) {
  const busy = cell.payments > 0 || cell.deadlines > 0 || cell.appointments > 0
  const weight = weightOf(cell, max)
  const dayNumber = Number(cell.date.slice(8, 10))

  return (
    <button
      onClick={busy ? onOpen : undefined}
      disabled={!busy}
      aria-label={`${dayNumber}: ${cell.payments} betalingen, ${cell.deadlines} deadlines, ${cell.appointments} afspraken`}
      className={`aspect-square rounded-lg flex flex-col items-center justify-center gap-1 transition-colors ${
        cell.inMonth ? '' : 'opacity-30'
      } ${cell.isToday ? 'ring-1 ring-ink' : ''}`}
      // Ink at low alpha rather than a colour: the weight has to stay under
      // the day number without ever competing with it for attention, and it
      // must still read on a greyscale screenshot.
      style={weight > 0 ? { backgroundColor: `rgba(26, 26, 26, ${(0.04 + weight * 0.14).toFixed(3)})` } : undefined}
    >
      <span className={`text-sm tabular-nums ${cell.isToday ? 'font-semibold' : ''}`}>
        {dayNumber}
      </span>
      <span className="flex items-center gap-0.5 h-2">
        {cell.payments > 0 && <span className="w-3 h-1 rounded-full bg-ink" aria-hidden />}
        {cell.deadlines > 0 && (
          <span className="w-1.5 h-1.5 rounded-full border-[1.5px] border-ink" aria-hidden />
        )}
        {cell.appointments > 0 && <span className="w-1.5 h-1.5 bg-ink" aria-hidden />}
      </span>
    </button>
  )
}
